import React from "react";
import { createRoot } from "react-dom/client";
import { ReplayWidget } from "./ReplayWidget";
import HeroExamples from "./components/hero/HeroExamples";
import "./styles/replay.css";

interface MountOptions {
  selector?: string;
}

function mountWidget(
  selector: string,
  component: React.ComponentType,
  tag: string
): (() => void) | undefined {
  const container = document.querySelector(selector);
  if (!container) return undefined;

  if (container.hasAttribute("data-githits-mounted")) {
    console.warn(`[${tag}] Container "${selector}" already mounted`);
    return undefined;
  }
  container.setAttribute("data-githits-mounted", "true");
  container.classList.add("githits-replay");

  const root = createRoot(container);
  root.render(React.createElement(component));

  return () => {
    root.unmount();
    container.removeAttribute("data-githits-mounted");
    container.classList.remove("githits-replay");
  };
}

function mount(options: MountOptions = {}): (() => void) | undefined {
  const selector = options.selector ?? "#githits-replay-cards";
  return mountWidget(selector, ReplayWidget, "githits-replay-cards");
}

function mountHeroExamples(
  options: MountOptions = {}
): (() => void) | undefined {
  const selector = options.selector ?? "#githits-hero-examples";
  return mountWidget(selector, HeroExamples, "githits-hero-examples");
}

// Auto-mount on DOMContentLoaded
function autoMount() {
  mount();
  mountHeroExamples();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", autoMount);
} else {
  autoMount();
}

// Expose for manual mounting
declare global {
  interface Window {
    GithitsReplayCards?: { mount: typeof mount };
    GithitsHeroExamples?: { mount: typeof mountHeroExamples };
  }
}
window.GithitsReplayCards = { mount };
window.GithitsHeroExamples = { mount: mountHeroExamples };
